// Adding the JSON grammar
import "prism-react-editor/prism/languages/json";

// Adds comment toggling and auto-indenting for JSON
import "prism-react-editor/languages/json";

import "prism-react-editor/layout.css";
import "prism-react-editor/themes/github-dark.css";

// Required by the basic setup
import "prism-react-editor/search.css";

import { Alert, Col, Container, Row } from "react-bootstrap";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, Outlet } from "react-router-dom";
import { events } from "../../data/events";
import { getLiveLanguages } from "../../models/company";
import { MailTemplatePath } from "../../models/mail-template";
import { useAppSelector } from "../../store";
import { companyActions } from "../../store/company-slice";
import { fetchMailTemplateSummary } from "../../utils/http/mail-templates";
import Menu from "./Menu";
import SendTestMailModal from "./SendTestMailModal";
import Summary from "./Summary";

function Root() {
  const dispatch = useDispatch();

  const [showSendTestModal, setShowSendTestModal] = useState("HIDDEN");
  // The paths selected in the Summary, used when sending test mails in bulk
  const [bulkTestPaths, setBulkTestPaths] = useState<any[]>([]);

  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const activeLiveLanguages = useMemo(
    () => getLiveLanguages(activeCompany?.info?.languages),
    [activeCompany],
  );

  const { data: summary } = useQuery({
    queryKey: ["mailTemplates", "summary"],
    queryFn: ({ signal }) => fetchMailTemplateSummary({ signal }),
    staleTime: 2000,
  });

  useEffect(() => {
    if (!summary) return;

    const allBlocks: string[] = [
      ...new Set<string>(summary.maintained.filter((c: any) => c.event === "replacements").map((c: any) => c.target)),
    ];
    const allBlocksAndTemplates = {
      REPLACEMENTS: {
        replacements: {
          blocks: allBlocks.sort(),
        },
      },
      ...events,
    };
    dispatch(companyActions.setBlocksAndTemplates(allBlocksAndTemplates));

    // Save for which paths we have drafts
    const templateSummariesSorted: any[] = [];
    Object.keys(allBlocksAndTemplates).forEach((automation) => {
      Object.keys(allBlocksAndTemplates[automation]).forEach((event) => {
        Object.keys(allBlocksAndTemplates[automation][event]).forEach((action) => {
          allBlocksAndTemplates[automation][event][action].forEach((target: string) => {
            let versions: { draft: string[]; live: string[] } = {
              draft: [],
              live: [],
            };
            (Object.keys(versions) as Array<"draft" | "live">).forEach((version) => {
              activeLiveLanguages.forEach((language) => {
                const foundTemplate = summary.maintained.find(
                  (t: MailTemplatePath) =>
                    t.language === language &&
                    t.event === event &&
                    t.action === action &&
                    t.target === target &&
                    t.version == version,
                );
                if (foundTemplate && !foundTemplate.empty) versions[version].push(language);
              });
            });
            Object.entries(versions).forEach(([version, languages]) => {
              if (languages.length > 0) {
                templateSummariesSorted.push({
                  automation,
                  event,
                  action,
                  target,
                  version,
                  languages: languages,
                });
              }
            });
          });
        });
      });
    });

    const templatesConfigSorted: any[] = [];
    Object.keys(events).forEach((automation) => {
      Object.keys(events[automation]).forEach((event) => {
        Object.keys(events[automation][event]).forEach((action) => {
          events[automation][event][action].forEach((target: string) => {
            let foundTemplate = summary.config.find(
              (t: any) => t.event === event && t.action === action && t.target === target,
            );
            if (foundTemplate) {
              foundTemplate = {
                ...foundTemplate,
                automation,
              };
              templatesConfigSorted.push(foundTemplate);
            }
          });
        });
      });
    });

    dispatch(
      companyActions.setTemplateSummary({
        maintained: templateSummariesSorted,
        config: templatesConfigSorted,
      }),
    );
  }, [summary, activeCompany, activeLiveLanguages, dispatch]);

  return (
    <div>
      <div className="mb-4">
        <h3 className="mb-1">Mail Templates (v3)</h3>
      </div>
      {!activeCompany?.integrations?.sendgrid ? (
        <Alert variant="danger" className="mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          No Sendgrid integration is configured, so no mails will be sent out. A Sendgrid integration needs to be added
          first (it can be in inactive or onboarding status).
        </Alert>
      ) : (
        activeCompany.integrations.sendgrid.status !== "active" && (
          <Alert variant="danger" className="mb-4">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            The Sendgrid integration is not active. Test mails will be sent out via the Colibry sendgrid account (domain
            colibry.ai).
          </Alert>
        )
      )}
      <hr className="mb-4" />
      <Container className="p-3">
        <Row>
          {activeLiveLanguages.length === 0 && (
            <>
              <Col>
                Please <Link to={`/companies/${activeCompany?.name}`}>configure</Link> the languages that apply to this
                customer.
              </Col>
            </>
          )}
          {activeLiveLanguages.length > 0 && (
            <>
              <Col xs="3">
                <Menu />
              </Col>
              <Col xs="9">
                <Summary
                  setShowSendTestModal={setShowSendTestModal}
                  setBulkTestPaths={setBulkTestPaths}
                />
                <Outlet context={{ showSendTestModal, setShowSendTestModal }} />
                <SendTestMailModal
                  showSendTestModal={showSendTestModal}
                  setShowSendTestModal={setShowSendTestModal}
                  bulkTestPaths={bulkTestPaths}
                />
              </Col>
            </>
          )}
        </Row>
      </Container>
    </div>
  );
}

export default Root;
